import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  Grid,
} from '@mui/material';
import { FilterList, Error, CheckCircle, Pending, Warning, Person } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GetReceptionsVm } from 'api/reception/get-receptions.vm';
import { getReceptions } from 'api/reception/get-receptions';
import { ReceptionVm, ReceptionStatus, ReceptionType } from 'api/reception/reception.vm';
import { GetWorkerVm } from 'api/management/get-worker.vm';
import { getWorker } from 'api/management/get-worker';
import { GetWarehousesVm } from 'api/warehouse/get-warehouses.vm';
import { getWarehouses } from 'api/warehouse/get-warehouses';
import SupplierSelectionDialog from 'components/SupplierSelectionDialog';
import WorkerSelectionDialog from 'components/WorkerSelectionDialog';

const ReceptionProcessesPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [receptions, setReceptions] = useState<ReceptionVm[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextOffset, setNextOffset] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [supplierName, setSupplierName] = useState('');
  const [receiverDialogOpen, setReceiverDialogOpen] = useState(false);
  const [selectedReceiver, setSelectedReceiver] = useState<GetWorkerVm | null>(null);
  const [warehouses, setWarehouses] = useState<{ id: string; name: string }[]>([]);

  // Estado inicial dos filtros da URL
  const statusFilter = (searchParams.get('status') as ReceptionStatus | null) || null;
  const supplierFilter = searchParams.get('supplier') || '';
  const receiverFilter = searchParams.get('receiver') || '';
  const warehouseFilter = searchParams.get('warehouse') || '';

  const updateSearchParams = (newParams: { status?: ReceptionStatus | null; supplier?: string; receiver?: string; warehouse?: string }) => {
    const params = new URLSearchParams(searchParams);

    if (newParams.status !== undefined) {
      newParams.status ? params.set('status', newParams.status) : params.delete('status');
    }
    if (newParams.supplier !== undefined) {
      newParams.supplier ? params.set('supplier', newParams.supplier) : params.delete('supplier');
    }
    if (newParams.receiver !== undefined) {
      newParams.receiver ? params.set('receiver', newParams.receiver) : params.delete('receiver');
    }
    if (newParams.warehouse !== undefined) {
      newParams.warehouse ? params.set('warehouse', newParams.warehouse) : params.delete('warehouse');
    }

    setSearchParams(params);
  };

  const fetchReceptions = (offset: string | null, reset: boolean = false) => {
    setLoading(true);

    getReceptions(offset || '', statusFilter, supplierFilter, receiverFilter || null, warehouseFilter || null, {
      success: (response: GetReceptionsVm) => {
        setHasMore(response.nextOffset !== null && response.nextOffset !== '');
        setNextOffset(response.nextOffset);
        setReceptions(reset ? response.objects : [...receptions, ...response.objects]);
        setLoading(false);
      },
      error: (err) => {
        console.error('Falha ao obter receções:', err);
        setLoading(false);
      },
    });
  };

  const fetchWarehouses = () => {
    getWarehouses('', {
      success: (response: GetWarehousesVm) => {
        setWarehouses(response.objects);
      },
      error: (err) => {
        console.error('Falha ao obter armazéns:', err);
      },
    });
  };

  useEffect(() => {
    fetchWarehouses();
  }, []);

  useEffect(() => {
    fetchReceptions(null, true);
  }, [statusFilter, supplierFilter, receiverFilter, warehouseFilter]);

  // Load receiver from URL parameter
  useEffect(() => {
    if (receiverFilter && !selectedReceiver) {
      getWorker(receiverFilter, {
        success: (worker) => {
          setSelectedReceiver(worker);
        },
        error: (err) => {
          console.error('Falha ao carregar recetor:', err);
        },
      });
    } else if (!receiverFilter && selectedReceiver) {
      setSelectedReceiver(null);
    }
  }, [receiverFilter]);

  const handleLoadMore = () => {
    if (!hasMore || !nextOffset) return;
    fetchReceptions(nextOffset);
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleStatusFilter = (status: ReceptionStatus | null) => {
    updateSearchParams({ status });
    handleFilterClose();
  };

  const handleSupplierSelect = (supplierId: string, supplierName: string) => {
    updateSearchParams({ supplier: supplierId });
    setSupplierName(supplierName);
    setSupplierDialogOpen(false);
  };

  const handleClearSupplierFilter = () => {
    updateSearchParams({ supplier: '' });
    setSupplierName('');
  };

  const handleReceiverFilter = (receiver: GetWorkerVm | null) => {
    setSelectedReceiver(receiver);
    updateSearchParams({ receiver: receiver?.id || '' });
    setReceiverDialogOpen(false);
  };

  const getStatusChip = (status: ReceptionStatus) => {
    switch (status) {
      case ReceptionStatus.OPEN:
        return <Chip icon={<Pending fontSize="small" />} label="Aberta" color="info" size="small" />;
      case ReceptionStatus.CLOSED:
        return <Chip icon={<CheckCircle fontSize="small" />} label="Fechada" color="success" size="small" />;
      case ReceptionStatus.CLOSED_WITH_ERROR:
        return <Chip icon={<Error fontSize="small" />} label="Fechada com Erro" color="error" size="small" />;
      case ReceptionStatus.CANCELED:
        return <Chip icon={<Warning fontSize="small" />} label="Cancelada" color="warning" size="small" />;
      case ReceptionStatus.PENDING_INTEGRATION:
        return <Chip icon={<Pending fontSize="small" />} label="Pendente Integração" color="secondary" size="small" />;
      default:
        return <Chip label="Desconhecido" size="small" />;
    }
  };

  const getReceptionTypeLabel = (type: ReceptionType) => {
    switch (type) {
      case ReceptionType.BY_SUPPLIER:
        return 'Por Fornecedor';
      case ReceptionType.BY_ORDER:
        return 'Por Encomenda';
      default:
        return 'Desconhecido';
    }
  };

  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleRowClick = (receptionId: string) => {
    navigate(`/reception/reception-processes/${encodeURIComponent(receptionId)}?${searchParams.toString()}`);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          Processos de Receção
        </Typography>
      </Box>

      <Paper sx={{ mb: 2, p: 2, borderRadius: 2, boxShadow: theme.shadows[1] }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth>
              <InputLabel id="warehouse-select-label">Armazém</InputLabel>
              <Select
                labelId="warehouse-select-label"
                value={warehouseFilter || ''}
                label="Armazém"
                onChange={(e) => {
                  updateSearchParams({ warehouse: e.target.value });
                }}
                sx={{
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              >
                <MenuItem value="">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>Todos os Armazéns</Box>
                </MenuItem>
                {warehouses.map((warehouse) => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{warehouse.name}</Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={8} md={9}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {supplierName ? (
                  <Chip label={supplierName} onDelete={handleClearSupplierFilter} sx={{ mr: 1 }} />
                ) : (
                  <Button variant="outlined" onClick={() => setSupplierDialogOpen(true)} sx={{ mr: 1 }}>
                    Filtrar por Fornecedor
                  </Button>
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {selectedReceiver ? (
                  <Chip
                    icon={<Person fontSize="small" />}
                    label={selectedReceiver.name}
                    onDelete={() => handleReceiverFilter(null)}
                    sx={{ mr: 1 }}
                  />
                ) : (
                  <Button variant="outlined" onClick={() => setReceiverDialogOpen(true)} sx={{ mr: 1 }}>
                    Filtrar por Recetor
                  </Button>
                )}
              </Box>

              <IconButton aria-label="filter" onClick={handleFilterClick} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                <FilterList />
              </IconButton>
            </Box>
          </Grid>
        </Grid>

        <Menu anchorEl={filterAnchorEl} open={Boolean(filterAnchorEl)} onClose={handleFilterClose}>
          <MenuItem onClick={() => handleStatusFilter(null)}>Todos os Estados</MenuItem>
          <MenuItem onClick={() => handleStatusFilter(ReceptionStatus.OPEN)}>Aberta</MenuItem>
          <MenuItem onClick={() => handleStatusFilter(ReceptionStatus.CLOSED)}>Fechada</MenuItem>
          <MenuItem onClick={() => handleStatusFilter(ReceptionStatus.CLOSED_WITH_ERROR)}>Fechada com Erro</MenuItem>
          <MenuItem onClick={() => handleStatusFilter(ReceptionStatus.CANCELED)}>Cancelada</MenuItem>
          <MenuItem onClick={() => handleStatusFilter(ReceptionStatus.PENDING_INTEGRATION)}>Pendente Integração</MenuItem>
        </Menu>
      </Paper>

      <SupplierSelectionDialog open={supplierDialogOpen} onClose={() => setSupplierDialogOpen(false)} onSelect={handleSupplierSelect} />

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: theme.palette.grey[800] }}>
            <TableRow>
              <TableCell>Fornecedor</TableCell>
              <TableCell>Data de Criação</TableCell>
              <TableCell>Recetor</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Estado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {receptions.map((reception) => (
              <TableRow key={reception.id} hover onClick={() => handleRowClick(reception.id)} sx={{ cursor: 'pointer' }}>
                <TableCell>{reception.supplierName}</TableCell>
                <TableCell>{formatDate(reception.createdAt)}</TableCell>
                <TableCell>{reception.receiverName || '-'}</TableCell>
                <TableCell>{getReceptionTypeLabel(reception.type)}</TableCell>
                <TableCell>{getStatusChip(reception.status)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        {loading ? (
          <CircularProgress />
        ) : hasMore ? (
          <Button variant="outlined" onClick={handleLoadMore} disabled={loading || !nextOffset}>
            Carregar mais
          </Button>
        ) : receptions.length > 0 ? (
          <Typography variant="body2" color="textSecondary">
            Não existem mais receções para carregar
          </Typography>
        ) : (
          <Typography variant="body2" color="textSecondary">
            Nenhuma receção encontrada
          </Typography>
        )}
      </Box>

      <WorkerSelectionDialog
        open={receiverDialogOpen}
        onClose={() => setReceiverDialogOpen(false)}
        onSelect={handleReceiverFilter}
        selectedWorkerId={selectedReceiver?.id}
        title="Selecionar Recetor"
      />
    </Box>
  );
};

export default ReceptionProcessesPage;
